import React, { useState, useEffect, useRef, useCallback } from 'react';
import BaseLayout from './BaseLayout';
import FlameLoader from './FlameLoader';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../api';

const TABS = [
  { id: 'notifications', label: 'Notifications', icon: 'fa fa-bell' },
  { id: 'messages',      label: 'Messages',      icon: 'fa fa-comments' },
  { id: 'sent',          label: 'Sent',           icon: 'fa fa-paper-plane-o' },
];

function fmtTime(ts) {
  if (!ts) return '';
  const d = new Date(ts + 'Z'); // backend stores as UTC string
  const now = new Date();
  const diff = now - d;
  if (diff < 60000)     return 'just now';
  if (diff < 3600000)   return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000)  return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  return d.toLocaleDateString();
}

function InboxAvatar({ name, picture, size = 38 }) {
  const [imgErr, setImgErr] = useState(false);
  const initials = (name || '?').split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();
  if (picture && !imgErr) {
    return (
      <img
        src={picture}
        alt={name}
        className="inbx-avatar inbx-avatar--img"
        style={{ width: size, height: size, minWidth: size }}
        onError={() => setImgErr(true)}
      />
    );
  }
  return (
    <div className="inbx-avatar" style={{ width: size, height: size, minWidth: size, fontSize: size * 0.38 }}>
      {initials}
    </div>
  );
}

const NotifItem = ({ item, onRead, onDelete, onAcceptFriend, onDeclineFriend, onOpenConv, navigate }) => {
  const isAchievement = item.is_achievement;
  const isFriendReq   = item.is_friend_request;
  const isMessage     = !isAchievement && !isFriendReq;
  const typeIcon  = isAchievement ? 'fa-trophy' : isFriendReq ? 'fa-user-plus' : 'fa-envelope';
  const typeLabel = isAchievement ? 'Achievement'    : isFriendReq ? 'Friend Request' : 'Message';
  const typeClass = isAchievement ? 'inbx-notif--achievement' : isFriendReq ? 'inbx-notif--friend' : 'inbx-notif--message';

  return (
    <div className={`inbx-notif-item ${typeClass}${!item.viewed ? ' inbx-notif-item--unread' : ''}`}>
      <div className="inbx-notif-icon">
        <i className={`fa ${typeIcon}`} />
      </div>
      <div className="inbx-notif-body">
        <div className="inbx-notif-top">
          <span className="inbx-notif-label">{typeLabel}</span>
          {!item.viewed && <span className="inbx-unread-dot" />}
          <span className="inbx-notif-time">{fmtTime(item.timestamp)}</span>
        </div>
        {!isAchievement && (
          <div className="inbx-notif-sender">
            {item.sender_username
              ? <span className="inbx-notif-sender-name" onClick={() => navigate(`/user/${item.sender_username}`)}>{item.sender_username}</span>
              : <span className="inbx-notif-sender-name">{item.sender_name}</span>}
          </div>
        )}
        <div className="inbx-notif-content">{item.content}</div>
        <div className="inbx-notif-actions">
          {isAchievement && (
            <button className="inbx-action-btn inbx-action-btn--gold" type="button"
              onClick={() => { onRead(item.id); navigate(item.achievement_key ? `/achievements/${item.achievement_key}` : '/achievements'); }}>
              <i className="fa fa-trophy" /> View Achievement
            </button>
          )}
          {isFriendReq && (
            <>
              <button className="inbx-action-btn inbx-action-btn--accept" type="button"
                onClick={() => onAcceptFriend(item.id, item.sender_id)}>
                <i className="fa fa-check" /> Accept
              </button>
              <button className="inbx-action-btn inbx-action-btn--decline" type="button"
                onClick={() => onDeclineFriend(item.id)}>
                <i className="fa fa-times" /> Decline
              </button>
            </>
          )}
          {isMessage && (
            <button className="inbx-action-btn" type="button"
              onClick={() => { onRead(item.id); onOpenConv(item.sender_id); }}>
              <i className="fa fa-reply" /> Reply
            </button>
          )}
          {!isFriendReq && (
            <button className="inbx-action-btn inbx-action-btn--dismiss" type="button"
              onClick={() => onDelete(item.id)} title="Dismiss">
              <i className="fa fa-trash" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const InboxPage = () => {
  const { authData } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = authData.token;
  const headers = { Authorization: `Bearer ${token}` };

  const [activeTab, setActiveTab] = useState('notifications');

  // — Notifications —
  const [notifications, setNotifications]   = useState([]);
  const [notifPage, setNotifPage]           = useState(1);
  const [notifHasNext, setNotifHasNext]     = useState(false);
  const [notifLoading, setNotifLoading]     = useState(false);
  const [notifFilter, setNotifFilter]       = useState('all');

  // — Messages —
  const [conversations, setConversations]   = useState([]);
  const [convsLoading, setConvsLoading]     = useState(false);
  const [selectedConvId, setSelectedConvId] = useState(null);
  const [thread, setThread]                 = useState([]);
  const [threadUser, setThreadUser]         = useState(null);
  const [threadLoading, setThreadLoading]   = useState(false);
  const [replyText, setReplyText]           = useState('');
  const [sending, setSending]               = useState(false);
  const [mobileShowThread, setMobileShowThread] = useState(false);
  const threadEndRef = useRef(null);

  // — Sent —
  const [sent, setSent]             = useState([]);
  const [sentPage, setSentPage]     = useState(1);
  const [sentHasNext, setSentHasNext] = useState(false);
  const [sentLoading, setSentLoading] = useState(false);

  // Handle URL params on mount
  useEffect(() => {
    const tab    = searchParams.get('tab');
    const withId = searchParams.get('with');
    if (tab === 'messages') {
      setActiveTab('messages');
      if (withId) openConversation(parseInt(withId, 10));
    } else if (tab === 'sent') {
      setActiveTab('sent');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load data when tab changes
  useEffect(() => {
    if (activeTab === 'notifications') loadNotifications(1, true);
    if (activeTab === 'messages') loadConversations();
    if (activeTab === 'sent') loadSent(1, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Auto-scroll thread to newest message
  useEffect(() => {
    if (threadEndRef.current) {
      threadEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [thread]);

  const loadNotifications = async (page = 1, reset = false) => {
    setNotifLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/user/all-notifications?page=${page}&per_page=25`, { headers });
      const items = res.data.notifications || [];
      setNotifications(prev => reset ? items : [...prev, ...items]);
      setNotifPage(page);
      setNotifHasNext(res.data.has_next);
    } catch (e) { console.error(e); }
    finally { setNotifLoading(false); }
  };

  const loadConversations = async () => {
    setConvsLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/user/conversations`, { headers });
      setConversations(res.data.conversations || []);
    } catch (e) { console.error(e); }
    finally { setConvsLoading(false); }
  };

  const openConversation = useCallback(async (userId) => {
    setSelectedConvId(userId);
    setMobileShowThread(true);
    setThreadLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/user/conversation/${userId}`, { headers });
      setThread(res.data.messages || []);
      setThreadUser(res.data.other_user || null);
      loadConversations(); // refresh unread counts
    } catch (e) { console.error(e); }
    finally { setThreadLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const sendReply = async () => {
    if (!replyText.trim() || !selectedConvId || sending) return;
    setSending(true);
    try {
      await axios.post(`${API_BASE}/user/send-message`, { recipient_id: selectedConvId, content: replyText.trim() }, { headers });
      setReplyText('');
      const res = await axios.get(`${API_BASE}/user/conversation/${selectedConvId}`, { headers });
      setThread(res.data.messages || []);
      loadConversations();
    } catch (e) { console.error(e); }
    finally { setSending(false); }
  };

  const loadSent = async (page = 1, reset = false) => {
    setSentLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/user/sent-messages?page=${page}&per_page=25`, { headers });
      const items = res.data.messages || [];
      setSent(prev => reset ? items : [...prev, ...items]);
      setSentPage(page);
      setSentHasNext(res.data.has_next);
    } catch (e) { console.error(e); }
    finally { setSentLoading(false); }
  };

  const handleMarkRead = async (id) => {
    try {
      await axios.post(`${API_BASE}/user/view-message/${id}`, {}, { headers });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, viewed: true } : n));
    } catch (e) {}
  };

  const handleDeleteNotif = async (id) => {
    try {
      await axios.delete(`${API_BASE}/user/delete-message/${id}`, { headers });
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (e) {}
  };

  const handleAcceptFriend = async (msgId, senderId) => {
    try {
      await axios.post(`${API_BASE}/user/add-friend/${authData.user?.id}/${senderId}`);
      await axios.delete(`${API_BASE}/user/delete-friend-request/${msgId}`, { headers });
      setNotifications(prev => prev.filter(n => n.id !== msgId));
    } catch (e) {}
  };

  const handleDeclineFriend = async (msgId) => {
    try {
      await axios.delete(`${API_BASE}/user/delete-friend-request/${msgId}`, { headers });
      setNotifications(prev => prev.filter(n => n.id !== msgId));
    } catch (e) {}
  };

  const handleOpenConvFromNotif = (senderId) => {
    setActiveTab('messages');
    openConversation(senderId);
  };

  const filteredNotifs = notifications.filter(n => {
    if (notifFilter === 'friend')      return n.is_friend_request;
    if (notifFilter === 'achievement') return n.is_achievement;
    if (notifFilter === 'message')     return !n.is_friend_request && !n.is_achievement;
    return true;
  });

  // ─── Render panels ────────────────────────────────────────────
  const renderNotifications = () => (
    <div className="inbx-panel">
      <div className="inbx-filter-bar">
        {[['all','All'],['message','Messages'],['friend','Friend Requests'],['achievement','Achievements']].map(([key, label]) => (
          <button key={key} type="button"
            className={`inbx-filter-chip${notifFilter === key ? ' inbx-filter-chip--active' : ''}`}
            onClick={() => setNotifFilter(key)}>
            {label}
          </button>
        ))}
      </div>
      {notifLoading && notifications.length === 0 ? (
        <div className="inbx-loading"><FlameLoader /></div>
      ) : filteredNotifs.length === 0 ? (
        <div className="inbx-empty">
          <i className="fa fa-bell-slash-o inbx-empty-icon" />
          <p>No notifications{notifFilter !== 'all' ? ' in this category' : ''}</p>
        </div>
      ) : (
        <div className="inbx-list">
          {filteredNotifs.map(n => (
            <NotifItem key={n.id} item={n}
              onRead={handleMarkRead} onDelete={handleDeleteNotif}
              onAcceptFriend={handleAcceptFriend} onDeclineFriend={handleDeclineFriend}
              onOpenConv={handleOpenConvFromNotif} navigate={navigate} />
          ))}
          {notifHasNext && (
            <button className="inbx-load-more" type="button" disabled={notifLoading}
              onClick={() => loadNotifications(notifPage + 1)}>
              {notifLoading ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      )}
    </div>
  );

  const renderMessages = () => (
    <div className={`inbx-msg-layout${mobileShowThread && selectedConvId ? ' inbx-msg-layout--thread-open' : ''}`}>
      {/* Conversation list */}
      <div className="inbx-conv-list">
        {convsLoading ? (
          <div className="inbx-loading"><FlameLoader /></div>
        ) : conversations.length === 0 ? (
          <div className="inbx-empty inbx-empty--sm">
            <i className="fa fa-comments-o inbx-empty-icon" />
            <p>No conversations yet</p>
          </div>
        ) : conversations.map(conv => (
          <button key={conv.user_id} type="button"
            className={`inbx-conv-item${selectedConvId === conv.user_id ? ' inbx-conv-item--active' : ''}`}
            onClick={() => openConversation(conv.user_id)}>
            <InboxAvatar name={`${conv.firstName || ''} ${conv.lastName || ''}`} picture={conv.profile_picture} />
            <div className="inbx-conv-info">
              <div className="inbx-conv-name">
                {conv.username || `${conv.firstName} ${conv.lastName}`}
                {conv.unread_count > 0 && <span className="inbx-unread-badge">{conv.unread_count}</span>}
              </div>
              <div className="inbx-conv-preview">
                {conv.last_from_me && <span className="inbx-conv-you">You: </span>}
                {conv.last_message?.length > 50 ? conv.last_message.slice(0, 50) + '…' : conv.last_message}
              </div>
            </div>
            <div className="inbx-conv-time">{fmtTime(conv.last_message_time)}</div>
          </button>
        ))}
      </div>

      {/* Thread pane */}
      <div className="inbx-thread">
        {selectedConvId && threadUser ? (
          <>
            <div className="inbx-thread-header">
              <button type="button" className="inbx-back-btn d-lg-none"
                onClick={() => { setMobileShowThread(false); setSelectedConvId(null); }}>
                <i className="fa fa-arrow-left" />
              </button>
              <Link to={`/user/${threadUser.username}`} className="inbx-thread-name">
                {threadUser.username || `${threadUser.firstName} ${threadUser.lastName}`}
              </Link>
            </div>
            <div className="inbx-messages">
              {threadLoading ? (
                <div className="inbx-loading"><FlameLoader /></div>
              ) : thread.length === 0 ? (
                <div className="inbx-empty"><p>No messages yet.</p></div>
              ) : thread.map(msg => (
                <div key={msg.id} className={`inbx-bubble${msg.from_me ? ' inbx-bubble--me' : ''}`}>
                  <div className="inbx-bubble-text">{msg.content}</div>
                  <div className="inbx-bubble-time">{fmtTime(msg.timestamp)}</div>
                </div>
              ))}
              <div ref={threadEndRef} />
            </div>
            <div className="inbx-reply">
              <textarea
                className="inbx-reply-input"
                placeholder="Write a message… (Enter to send, Shift+Enter for new line)"
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                rows={2}
              />
              <button className="inbx-reply-btn" type="button" disabled={!replyText.trim() || sending} onClick={sendReply}>
                {sending ? <i className="fa fa-spinner fa-spin" /> : <i className="fa fa-paper-plane" />}
              </button>
            </div>
          </>
        ) : (
          <div className="inbx-thread-empty">
            <i className="fa fa-comments-o" style={{ fontSize: '2.5rem', color: 'var(--text-dim)', marginBottom: 10 }} />
            <p>Select a conversation</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderSent = () => (
    <div className="inbx-panel">
      {sentLoading && sent.length === 0 ? (
        <div className="inbx-loading"><FlameLoader /></div>
      ) : sent.length === 0 ? (
        <div className="inbx-empty">
          <i className="fa fa-paper-plane-o inbx-empty-icon" />
          <p>No sent messages</p>
        </div>
      ) : (
        <div className="inbx-list">
          {sent.map(msg => (
            <div key={msg.id} className="inbx-sent-item">
              <div className="inbx-sent-to">
                <i className="fa fa-user-o" />
                <Link to={`/user/${msg.recipient_username}`} className="inbx-sent-name">
                  {msg.recipient_username || msg.recipient_name}
                </Link>
                {msg.viewed && <span className="inbx-seen-badge"><i className="fa fa-check-circle" /> Seen</span>}
              </div>
              <div className="inbx-sent-content">"{msg.content}"</div>
              <div className="inbx-sent-time">{fmtTime(msg.timestamp)}</div>
            </div>
          ))}
          {sentHasNext && (
            <button className="inbx-load-more" type="button" disabled={sentLoading}
              onClick={() => loadSent(sentPage + 1)}>
              {sentLoading ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <BaseLayout>
      <div className="inbx-wrap">
        <div className="inbx-header">
          <h1 className="inbx-title">Inbox</h1>
          <p className="inbx-subtitle">Notifications, messages, and conversations</p>
        </div>
        <div className="inbx-tabs">
          {TABS.map(tab => (
            <button key={tab.id} type="button"
              className={`inbx-tab${activeTab === tab.id ? ' inbx-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}>
              <i className={tab.icon} aria-hidden="true" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
        <div className="inbx-content">
          {activeTab === 'notifications' && renderNotifications()}
          {activeTab === 'messages'      && renderMessages()}
          {activeTab === 'sent'          && renderSent()}
        </div>
      </div>
    </BaseLayout>
  );
};

export default InboxPage;
